// Board storage is namespaced by the signed-in QLT user, so the next teacher on a shared board never sees the last one's work.

// the .ts extension keeps this loadable by node --test, which does not resolve bare specifiers
import { listKeys, readJson, removeKey, writeJson } from '../../api/storage.ts';

/** Teacher ids holding board data, least recently signed in first. */
const OWNERS_KEY = 'izqlt.board-owners';
/** Teachers whose board data a shared panel keeps; older ones are dropped. */
export const OWNER_LIMIT = 5;

/** Keys written before storage was per teacher; they belong to an unknown teacher. */
const LEGACY_KEYS = ['izqlt.board-opened', 'izqlt.board-hidden', 'izqlt.board-quizzes'];
const LEGACY_PREFIX = 'izqlt.board.';

let owner: string | null = null;
const listeners: Array<() => void> = [];

function ownerPrefix(id: string): string {
  return 'izqlt.teacher.' + encodeURIComponent(id) + '.';
}

export function isLegacyBoardKey(key: string): boolean {
  return LEGACY_KEYS.indexOf(key) >= 0 || key.indexOf(LEGACY_PREFIX) === 0;
}

/** Moves `id` to most recent and returns the ids past `limit` to drop. */
export function touchOwner(owners: string[], id: string, limit: number): { owners: string[]; evicted: string[] } {
  const next = owners.filter((item) => item !== id).concat([id]);
  const cut = Math.max(0, next.length - limit);
  return { owners: next.slice(cut), evicted: next.slice(0, cut) };
}

/** The storage key for `name` under the current teacher, or null when nobody signed in. */
export function boardKey(name: string): string | null {
  return owner === null ? null : ownerPrefix(owner) + name;
}

export function readBoardJson<T>(name: string): T | null {
  const key = boardKey(name);
  return key ? readJson<T>(key) : null;
}

export function writeBoardJson(name: string, value: unknown): void {
  const key = boardKey(name);
  if (key) writeJson(key, value);
}

function dropKeys(match: (key: string) => boolean): void {
  listKeys().filter(match).forEach(removeKey);
}

/** Called on sign-in only: sign-out keeps the owner so the board's unmount flush lands in the teacher who left. */
export function setBoardOwner(id: string | number | null): void {
  const next = id === null || id === undefined ? null : String(id);
  if (next !== null) {
    const stored = readJson<unknown>(OWNERS_KEY);
    const owners = Array.isArray(stored) ? stored.filter((item): item is string => typeof item === 'string') : [];
    const touched = touchOwner(owners, next, OWNER_LIMIT);
    touched.evicted.forEach((evicted) => {
      const prefix = ownerPrefix(evicted);
      dropKeys((key) => key.indexOf(prefix) === 0);
    });
    writeJson(OWNERS_KEY, touched.owners);
  }
  if (next === owner) return;
  owner = next;
  listeners.forEach((listener) => listener());
}

/** Stores holding board data in memory reload it from the new teacher's keys. */
export function onBoardOwnerChange(listener: () => void): void {
  listeners.push(listener);
}

/** Boot-time cleanup; idempotent, since namespaced keys never match. */
export function dropLegacyBoardKeys(): void {
  dropKeys(isLegacyBoardKey);
}
